import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { drawingService } from '../../../services/drawingService';
import { useLanguage } from '../../../contexts/LanguageContext';

const DrawingVersionsModal = ({ isOpen, onClose, drawing, onDownload }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    if (drawing?.drawing_number) {
      loadVersions();
    }
  }, [drawing]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const result = await drawingService?.getDrawingVersions(drawing?.site_id, drawing?.drawing_number);
      if (result?.data) {
        setVersions(result?.data);
      }
    } catch (err) {
      console.error('Error loading versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb?.toFixed(2)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="History" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('versionHistory')}</h2>
              <p className="text-sm text-muted-foreground">{drawing?.drawing_name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={t('close')}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            </div>
          ) : versions?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="FileImage" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">{t('noVersionsFound')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions?.map((version, index) => (
                <div
                  key={version?.id}
                  className={`border rounded-lg p-4 transition-smooth ${
                    version?.is_latest
                      ? 'border-accent bg-accent/5' :'border-border bg-card hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                        <span className="text-sm font-bold text-accent">v{version?.version_number}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{version?.drawing_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {t('uploaded')} {formatDate(version?.upload_date)}
                        </p>
                      </div>
                    </div>
                    {version?.is_latest && (
                      <span className="px-2 py-1 rounded-md text-xs font-medium bg-success/10 text-success">
                        {t('latest')}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('size')}</p>
                      <p className="text-sm font-medium text-foreground">{formatFileSize(version?.file_size)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('drawingNumber')}</p>
                      <p className="text-sm font-medium text-foreground">{version?.drawing_number}</p>
                    </div>
                  </div>

                  {version?.description && (
                    <p className="text-sm text-muted-foreground mb-3">{version?.description}</p>
                  )}

                  <Button
                    variant={version?.is_latest ? 'default' : 'outline'}
                    size="sm"
                    fullWidth
                    iconName="Download"
                    onClick={() => onDownload(version)}
                  >
                    {t('download')} v{version?.version_number}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            {t('close')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DrawingVersionsModal;